/**
 * Design Tab Component
 * Hierarchical control tree with drag-drop reorder and nested tab support.
 * In compact mode (embedded in three-panel layout) the toolbar is hidden.
 */

import React, { useCallback, useState } from 'react';
import { useUIStore } from '@atlas-forms/state-react';
import type { FormSchema, FormControl } from '@atlas-forms/types-js';
import { getDefaultLabel, getControlLabel } from '../../utils/controlUtils';
import '../../styles/tabs/DesignTab.css';

export interface DesignTabProps {
  schema: FormSchema;
  selectedControlId: string | null;
  onSelectControl: (id: string | null) => void;
  onAddControl: (control: FormControl) => void;
  onDeleteControl: (id: string) => void;
  onDuplicateControl: (id: string) => void;
  onReorderControls: (controlIds: string[]) => void;
  zoomLevel: number;
  showGrid: boolean;
  /** ID of the tab currently targeted for new field insertion */
  selectedTabId?: string | null;
  /** Callback to set/clear the targeted tab */
  onSelectTab?: (id: string | null) => void;
  /** Hide toolbar — set when embedded in the three-panel design view */
  compact?: boolean;
}

/** Short symbol for a control type shown in the tree row */
function getTypeSymbol(type: string): string {
  if (['text', 'textarea', 'email', 'url', 'password'].includes(type)) return 'Aa';
  if (type === 'number') return '##';
  if (['checkbox', 'switch'].includes(type)) return '☑';
  if (type === 'radio') return '◉';
  if (['select', 'multiselect', 'cascading-select', 'multi-select-search', 'tree-select'].includes(type)) return '▾';
  if (['date', 'datetime', 'date-range-picker', 'time-picker'].includes(type)) return '⏱';
  if (type === 'tab') return '⊞';
  if (['accordion', 'collapsible-panel', 'card-container', 'stepper', 'form-container'].includes(type)) return '⊟';
  if (type === 'button') return '▷';
  if (['json-editor', 'enhanced-json-editor', 'code-editor', 'javascript-block'].includes(type)) return '{}';
  if (type.includes('chart') || type.includes('graph') || type === 'analytics-dashboard') return '↗';
  if (['label', 'header'].includes(type)) return 'T';
  if (type === 'image') return '▣';
  if (['data-table', 'grid', 'table'].includes(type)) return '⊞';
  return '◈';
}

/**
 * Design Tab - Visual form builder with hierarchical control tree
 */
export const DesignTab: React.FC<DesignTabProps> = ({
  schema,
  selectedControlId,
  onSelectControl,
  onAddControl,
  onDeleteControl,
  onDuplicateControl,
  onReorderControls,
  zoomLevel,
  showGrid,
  selectedTabId,
  onSelectTab,
  compact = false,
}) => {
  const { theme } = useUIStore();
  const isDark = theme === 'dark';
  const [draggedOverId, setDraggedOverId] = useState<string | null>(null);
  const [draggedId, setDraggedId] = useState<string | null>(null);
  const [expandedTabIds, setExpandedTabIds] = useState<Set<string>>(new Set());

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>, targetId: string | null) => {
      e.preventDefault();

      // Check for new control being dragged from toolbar
      const jsonData = e.dataTransfer.getData('application/json');
      if (jsonData) {
        try {
          const data = JSON.parse(jsonData);
          if (data.type === 'new-control' && data.controlType) {
            const newControl: FormControl = {
              id: `control-${Date.now()}`,
              type: data.controlType,
              label: getDefaultLabel(data.controlType),
              order: targetId
                ? schema.controls.findIndex((c) => c.id === targetId)
                : schema.controls.length,
              required: false,
              readonly: false,
              disabled: false,
              hidden: false,
            };
            onAddControl(newControl);
            setDraggedId(null);
            setDraggedOverId(null);
            return;
          }
        } catch {
          // Not JSON, fall through to text/plain handling
        }
      }

      // Handle reordering existing controls (text/plain)
      const fromId = e.dataTransfer.getData('text/plain');
      if (fromId && fromId !== targetId) {
        const fromIdx = schema.controls.findIndex((c) => c.id === fromId);
        const toIdx = targetId
          ? schema.controls.findIndex((c) => c.id === targetId)
          : schema.controls.length;
        if (fromIdx !== -1) {
          const reordered = [...schema.controls];
          const [moved] = reordered.splice(fromIdx, 1);
          reordered.splice(Math.max(0, toIdx - (fromIdx < toIdx ? 1 : 0)), 0, moved);
          onReorderControls(reordered.map((c) => c.id));
        }
      }
      setDraggedId(null);
      setDraggedOverId(null);
    },
    [schema.controls, onAddControl, onReorderControls]
  );

  const toggleExpand = useCallback((controlId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setExpandedTabIds((prev) => {
      const next = new Set(prev);
      next.has(controlId) ? next.delete(controlId) : next.add(controlId);
      return next;
    });
  }, []);

  /** Render a single draggable control row */
  const renderControlRow = (control: FormControl, depth: number) => {
    const isSelected = selectedControlId === control.id;
    const isOver = draggedOverId === control.id;
    const isDragging = draggedId === control.id;
    const isTab = control.type === 'tab';
    const isFormContainer = control.type === 'form-container';
    const isExpandable = isTab || isFormContainer;
    const isExpanded = expandedTabIds.has(control.id);
    const tabCount = isTab ? ((control.config?.tabs as any[])?.length ?? 0) : (isFormContainer ? ((control.config?.childForms as any[])?.length ?? 0) : 0);

    return (
      <div
        draggable
        onClick={() => onSelectControl(control.id)}
        onContextMenu={(e) => { e.preventDefault(); onSelectControl(control.id); }}
        onDragStart={(e) => {
          e.stopPropagation();
          e.dataTransfer.effectAllowed = 'move';
          e.dataTransfer.setData('text/plain', control.id);
          setDraggedId(control.id);
        }}
        onDragOver={(e) => { e.stopPropagation(); handleDragOver(e); setDraggedOverId(control.id); }}
        onDragLeave={(e) => { e.stopPropagation(); setDraggedOverId(null); }}
        onDrop={(e) => { e.stopPropagation(); handleDrop(e, control.id); }}
        onDragEnd={() => { setDraggedId(null); setDraggedOverId(null); }}
        style={{
          display: 'flex', alignItems: 'center', gap: 6,
          padding: '8px 10px', paddingLeft: `${10 + depth * 16}px`,
          marginBottom: 2, borderRadius: 6,
          background: isSelected
            ? (isDark ? 'rgba(34,197,94,0.12)' : 'rgba(22,163,74,0.08)')
            : isOver ? (isDark ? 'rgba(34,197,94,0.06)' : 'rgba(22,163,74,0.04)') : 'transparent',
          border: `1px solid ${isOver ? '#16a34a' : isSelected ? (isDark ? 'rgba(34,197,94,0.35)' : 'rgba(22,163,74,0.4)') : (isDark ? 'rgba(34,197,94,0.4)' : 'rgba(22,163,74,0.3)')}`,
          cursor: isDragging ? 'grabbing' : 'grab',
          opacity: isDragging ? 0.4 : 1,
          boxShadow: isOver ? '0 -2px 0 0 #16a34a' : 'none',
          transition: 'background 0.1s, border-color 0.1s',
          userSelect: 'none' as const,
        }}
      >
        <span style={{ color: 'rgba(100,116,139,0.4)', fontSize: 12, flexShrink: 0 }}>⠿</span>

        {isExpandable ? (
          <button
            type="button"
            onClick={(e) => toggleExpand(control.id, e)}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              padding: '0 2px', fontSize: 12, lineHeight: 1,
              color: '#64748b', flexShrink: 0, display: 'flex', alignItems: 'center',
            }}
          >{isExpanded ? '▾' : '▸'}</button>
        ) : (
          <span style={{ width: 16, flexShrink: 0 }} />
        )}

        <span style={{
          fontSize: 10, fontWeight: 700, color: '#64748b',
          background: 'rgba(100,116,139,0.08)', borderRadius: 3,
          padding: '1px 4px', flexShrink: 0, fontFamily: 'monospace',
        }}>
          {getTypeSymbol(control.type)}
        </span>

        <span style={{
          fontSize: 13, fontWeight: isSelected ? 600 : 400,
          flex: 1, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis',
          color: isSelected ? '#16a34a' : undefined,
        }}>
          {control.label || control.id}
        </span>

        <span style={{ fontSize: 10, color: '#94a3b8', flexShrink: 0 }}>
          {control.type}
        </span>

        {isExpandable && tabCount > 0 && (
          <span style={{ fontSize: 10, color: '#475569', flexShrink: 0 }}>
            ({tabCount})
          </span>
        )}
      </div>
    );
  };

  /** Recursive tree: control row + nested tab sections or child forms */
  const renderControlTree = (control: FormControl, depth = 0): React.ReactNode => {
    const isTab = control.type === 'tab';
    const isFormContainer = control.type === 'form-container';
    const isExpandable = isTab || isFormContainer;
    const isExpanded = expandedTabIds.has(control.id);
    const tabs: any[] = isTab ? ((control.config?.tabs as any[]) ?? []) : [];
    const childForms: any[] = isFormContainer ? ((control.config?.childForms as any[]) ?? []) : [];

    return (
      <React.Fragment key={control.id}>
        {renderControlRow(control, depth)}

        {isTab && isExpanded && tabs.map((tab: any) => {
          const isTabTargeted = selectedTabId === tab.id;
          const nestedControls: FormControl[] = tab.controls || [];
          return (
            <React.Fragment key={tab.id}>
              <div
                onClick={(e) => {
                  e.stopPropagation();
                  onSelectTab?.(isTabTargeted ? null : tab.id);
                  onSelectControl(null);
                }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  padding: '5px 10px', paddingLeft: `${10 + (depth + 1) * 16}px`,
                  marginBottom: 2, borderRadius: 4, cursor: 'pointer',
                  background: isTabTargeted ? 'rgba(34,197,94,0.18)' : 'rgba(34,197,94,0.06)',
                  border: `1.5px solid ${isTabTargeted ? 'rgba(34,197,94,0.5)' : 'rgba(34,197,94,0.15)'}`,
                  fontSize: 12, fontWeight: 500,
                  color: isTabTargeted ? '#16a34a' : '#64748b',
                  userSelect: 'none' as const,
                }}
              >
                <span style={{ fontSize: 10, flexShrink: 0 }}>⊡</span>
                <span style={{ flex: 1 }}>{tab.label || tab.id}</span>
                <span style={{ fontSize: 10, opacity: 0.6 }}>
                  {nestedControls.length} field{nestedControls.length !== 1 ? 's' : ''}
                </span>
                {isTabTargeted && (
                  <span style={{ fontSize: 10, color: '#16a34a', fontStyle: 'italic' }}>target</span>
                )}
              </div>
              {nestedControls.map((tc) => renderControlTree(tc, depth + 2))}
            </React.Fragment>
          );
        })}

        {isFormContainer && isExpanded && childForms.map((child: any) => (
          <div
            key={child.formID}
            onClick={(e) => e.stopPropagation()}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '5px 10px', paddingLeft: `${10 + (depth + 1) * 16}px`,
              marginBottom: 2, borderRadius: 4, cursor: 'default',
              background: 'rgba(34,197,94,0.06)',
              border: `1.5px solid rgba(34,197,94,0.15)`,
              fontSize: 12, fontWeight: 400,
              color: '#64748b',
              userSelect: 'none' as const,
            }}
          >
            <span style={{ fontSize: 10, flexShrink: 0 }}>⊕</span>
            <span style={{ flex: 1 }}>{child.label || child.formID}</span>
            <span style={{ fontSize: 10, opacity: 0.6 }}>
              {child.dataBinding?.mode || 'shared-root'}
            </span>
          </div>
        ))}
      </React.Fragment>
    );
  };

  return (
    <div className="af-design-tab" data-theme={theme}>
      {!compact && (
        <div className="af-design-tab__toolbar">
          <div className="af-design-tab__toolbar-group">
            <span className="af-design-tab__toolbar-label">Controls: {schema.controls.length}</span>
          </div>
          <div className="af-design-tab__toolbar-group">
            {selectedControlId && (
              <>
                <button
                  onClick={() => onDuplicateControl(selectedControlId)}
                  className="af-design-tab__toolbar-btn"
                  title="Duplicate"
                >Duplicate</button>
                <button
                  onClick={() => { onDeleteControl(selectedControlId); onSelectControl(null); }}
                  className="af-design-tab__toolbar-btn af-design-tab__toolbar-btn--danger"
                  title="Delete"
                >Delete</button>
              </>
            )}
          </div>
        </div>
      )}

      <div
        className={`af-design-tab__canvas ${showGrid ? 'af-design-tab__canvas--grid' : ''}`}
        style={{ transform: `scale(${zoomLevel / 100})`, transformOrigin: 'top left' } as React.CSSProperties}
      >
        <div
          className="af-design-tab__canvas-content"
          onDragOver={handleDragOver}
          onDrop={(e) => handleDrop(e, null)}
        >
          {schema.controls.length === 0 ? (
            <div className="af-design-tab__empty-state">
              <p>No controls added yet</p>
              <p className="af-design-tab__empty-hint">
                {compact
                  ? '← Click a field type in the left panel to add it'
                  : 'Switch to Controls tab to add controls'}
              </p>
            </div>
          ) : (
            <div style={{ padding: 8 }}>
              {schema.controls.map((control) => renderControlTree(control))}
            </div>
          )}
        </div>
      </div>

      {!compact && (
        <div className="af-design-tab__status">
          <small>
            {selectedControlId
              ? <>Selected: {schema.controls.find((c) => c.id === selectedControlId)?.label || selectedControlId}</>
              : <>Click a control to select it, or drag to reorder</>
            }
          </small>
        </div>
      )}
    </div>
  );
};

DesignTab.displayName = 'DesignTab';

// getDefaultLabel and getControlLabel imported from ../../utils/controlUtils
